import {
  Box,
  Container,
  HStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  useDisclosure,
  Spinner
} from "@chakra-ui/react";
import { BellIcon } from "@chakra-ui/icons";
import { NavLink } from "react-router-dom";
import colors from "../helpers/colors";
import ChatDrawer from "./ChatDrawer/ChatDrawer";
import { useState, useMemo } from "react";

export default function Navbar({ currentUser, sendbirdNotifications = [], showCountLoader }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedNotification, setSelectedNotification] = useState(null);

  // Initialize from localStorage or current time
  const [lastViewedTimestamp, setLastViewedTimestamp] = useState(() => {
    const stored = localStorage.getItem('lastNotificationView');
    return stored ? parseInt(stored) : Date.now();
  });

  const handleNotificationClick = (notification) => {
    const latestMessage = notification.messages?.[notification.messages.length - 1];

    const sender = latestMessage?.sender;
    console.log("****sender****", latestMessage?.sender)

    if (!sender?.id) {
      console.warn("Notification missing sender userId", notification);
      return;
    }

    setSelectedNotification({
      id: latestMessage?.sender?.id,
      fullName: latestMessage?.sender?.name || "Unknown User",
      profileImageUrl: latestMessage?.sender?.profileImageUrl || "",
      channelUrl: notification.channelUrl
    });

    console.log("Notification:", notification);
    console.log("Sender:", sender);
    console.log("Latest Message:", latestMessage);
    onOpen();
  };


  const getUnreadCount = () => {
    return sendbirdNotifications.reduce((count, notification) => {
      const latestMessage = notification.messages[notification.messages.length - 1];
      return latestMessage?.timestamp > lastViewedTimestamp ? count + 1 : count;
    }, 0);
  };

  const handleMenuOpen = () => {
    const currentTime = Date.now();
    setLastViewedTimestamp(currentTime);
    localStorage.setItem('lastNotificationView', currentTime.toString());
  };

  const handleChatDrawerClose = () => {
    onClose();
    const currentTime = Date.now();
    setLastViewedTimestamp(currentTime);
    localStorage.setItem('lastNotificationView', currentTime.toString());

    setSelectedNotification(null);
  };

  const unreadCount = getUnreadCount();

  const sortedNotifications = useMemo(() => {
    return [...sendbirdNotifications].sort((a, b) => {
      const aTimestamp = a.messages[a.messages.length - 1]?.timestamp || 0;
      const bTimestamp = b.messages[b.messages.length - 1]?.timestamp || 0;
      return bTimestamp - aTimestamp;
    });
  }, [sendbirdNotifications]);

  return (
    <Box
      bg={"var(--gray-1000, #1D1D1D)"}
      borderBottom={`1px solid  ${colors.greyMedium}`}
    >
      <Container py="4" px="6" maxW="auto">
        <HStack justify="space-between">
          <NavLink to={"/"}>
            {!!currentUser && (
              <img
                src={currentUser?.agency?.logoUrl}
                alt="Logo"
                style={{ height: "32px" }}
              />
            )}
          </NavLink>

          <HStack spacing={4}>
            {/* Notification Dropdown */}
            <Menu onOpen={handleMenuOpen}>
              <Box position="relative">
                <MenuButton
                  as={IconButton}
                  icon={<BellIcon color={colors.white} />}
                  _hover={{ bg: colors.greyBorderDark }}
                  _active={{ bg: colors.greyBorderDark }}
                  variant="ghost"
                />
                {/* Show loader or count bubble */}
                {showCountLoader ? (
                  <Box
                    position="absolute"
                    top="-3px"
                    right="-3px"
                    width="18px"
                    height="18px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    zIndex={1}
                  >
                    <Spinner
                      size="xs"
                      thickness="2px"
                      speed="0.45s"
                      emptyColor="gray.200"
                    />
                  </Box>
                ) : (
                  unreadCount > 0 && (
                    <Box
                      position="absolute"
                      top="-3px"
                      right="-3px"
                      minWidth="18px"
                      height="18px"
                      backgroundColor="red.500"
                      color="white"
                      borderRadius="full"
                      fontSize="xs"
                      fontWeight="normal"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      zIndex={1}
                    >
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </Box>
                  )
                )}
              </Box>
              <MenuList h={"382px"} w={"400px"} overflowY={"scroll"}>
                {sendbirdNotifications.length === 0 ? (
                  <MenuItem>
                    <Box color="gray.400" fontSize="sm">No notifications</Box>
                  </MenuItem>
                ) : (
                  sortedNotifications.map((notification, index) => {
                    const latestMessage = notification.messages[notification.messages.length - 1];
                    const message = latestMessage?.message;
                    const senderName = latestMessage?.sender?.name;
                    const timestamp = latestMessage?.timestamp;
                    const isUnread = timestamp > lastViewedTimestamp;

                    return (
                      <MenuItem
                        key={notification.channelUrl || `notification-${index}`}
                        borderBottom={`1px solid ${colors.greyMedium}`}
                        onClick={() => handleNotificationClick(notification)}
                        position="relative"
                        bg={isUnread ? 'rgba(66, 153, 225, 0.08)' : 'transparent'}
                      >
                        <Box>
                          <Box fontWeight="bold">
                            {senderName || "Unknown Sender"}
                            {isUnread && (
                              <Box
                                as="span"
                                ml={2}
                                width="8px"
                                height="8px"
                                borderRadius="full"
                                backgroundColor="blue.500"
                                display="inline-block"
                              />
                            )}
                          </Box>
                          <Box fontSize="sm" color="gray.100">
                            {message || "No message content"}
                          </Box>
                          <Box fontSize="xs" color="gray.300">
                            {timestamp ? new Date(timestamp).toLocaleString() : ""}
                          </Box>
                        </Box>
                      </MenuItem>
                    );
                  })
                )}
              </MenuList>
            </Menu>
          </HStack>
        </HStack>
      </Container>

      {/* Chat Drawer */}
      {selectedNotification && (
        <ChatDrawer
          isOpen={isOpen}
          onClose={handleChatDrawerClose}
          currentUser={currentUser}
          candidate={selectedNotification}
          existingChannelUrl={selectedNotification.channelUrl}
        />
      )}
    </Box>
  );
}
